import { ACTIONS } from 'actions/requests/types';
import { RequestsApi } from 'api-client/apis/RequestsApi';
import { UserRequest } from 'api-client/models/UserRequest';
import classNames from 'classnames';
import Button from 'components/Buttons/Button';
import { ButtonIconWithCustomTouchArea } from 'components/Buttons/ButtonIcon';
import Checkbox from 'components/Inputs/Checkbox';
import Dropdown from 'components/Inputs/Dropdown';
import { Input, TextArea } from 'components/Inputs/Input';
import Label from 'components/Label/Label';
import SimpleToolTip from 'components/Tooltip/SimpleToolTip';
import P from 'components/Typography/P';
import {
    acceptableFileTypes,
    companyCodes,
    defaultRequestStatus,
    maxFileNameLength,
    maxFileSize,
    maxNumbersOfFiles,
    minFileSize
} from 'constants/caseMangement';
import useSelector from 'hooks/useSelector';
import useSnackbar from 'hooks/useSnackbar';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ReactComponent as InfoIcon } from 'static/icons/info-icon.svg';
import { ReactComponent as RemoveIcon } from 'static/icons/remove-cross.svg';
import { Option } from 'types';
import { SnackbarType } from 'types/snackbar';
import { getAuthConfiguration } from 'utils/getAuthConfiguration';
import { onOpenContentItem } from 'utils/route';

import styles from './RequestForm.module.scss';

export type IssueType = Option | null;
type CompanyCode = Option | null;
type Files = File[];

export interface RequestFormValues {
    issueType: IssueType;
    companyCode: CompanyCode;
    files: Files;
    summary: string;
    description: string;
    consentAccepted: boolean;
}
interface RequestFormProps {
    initialState?: Partial<RequestFormValues>;
    disabled?: boolean;
    classes?: {
        root?: string;
        content?: string;
        primary?: string;
        secondary?: string;
        label?: string;
        footer?: string;
    };
    availableIssueTypes: Option[];
    apiBaseUrl: string;
    onRequestCreated?(request: UserRequest): void;
}

export const RequestForm: React.FC<RequestFormProps> = ({
    initialState,
    disabled,
    classes,
    availableIssueTypes,
    apiBaseUrl,
    onRequestCreated,
}) => {
    const intl = useIntl();
    const servicesDescriptionContentID = "14692140620689";
    const [formValues, setFormValues] = React.useState<Partial<RequestFormValues>>(
        initialState || {},
    );
    const updateFormValues = (values: Partial<RequestFormValues>) =>
        setFormValues({ ...formValues, ...values });

    const {
        issueType,
        companyCode,
        files: formFiles,
        summary,
        description,
        consentAccepted,
    } = formValues;
    const files = formFiles || [];

    const valueFromEvent =
        (fn: (_: string) => void) =>
        (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) =>
            fn(event.target.value);

    const [errorMessage, setErrorMessage] = useState<React.ReactNode>(null);
    const [error, setError] = useState<boolean>(false);

    interface FileError {
        message: string;
        code: string;
    }
    interface FileRejection {
        file: File;
        errors: FileError[];
    }

    const onUpdateIssueType = (issueType: IssueType) => updateFormValues({ issueType });
    const onUpdateCompanyCode = (companyCode: CompanyCode) => updateFormValues({ companyCode });
    const onUpdateSummary = valueFromEvent((summary) => updateFormValues({ summary }));
    const onUpdateDescription = valueFromEvent((description) => updateFormValues({ description }));
    const onAddFiles = (newFiles: File[]) => {
        setFormValues({ ...formValues, files: [...files, ...newFiles] });
        setError(false);
    };

    const fileNameTooLong: string = intl.formatMessage({
        id: "case_management.request_form.file_name_too_long",
    })
    const fileTooLarge: string = intl.formatMessage({
        id: "case_management.request_form.file_too_large",
    })
    const fileTooSmall: string = intl.formatMessage({
        id: "case_management.request_form.file_too_small",
    })

    const validateFile = (file: File) => {
        if (file.name.length > maxFileNameLength) {
            return {
                code: fileNameTooLong,
                message: `The file name is a bit too long. Please keep it under ${maxFileNameLength} characters (about 15–20 words)`
            }
        }
        return null;
    };

    const renderDropzoneLink = (chunks: React.ReactNode) => (
        <a
            href="https://my.hr.wartsila.com/collection?#contentitem/4404095771281"
            target="_blank"
            rel="noopener noreferrer"
            className={styles["link-highlight"]}
        >
            {chunks}
        </a>
    );

    const onFileRejected = (file: FileRejection[]) => {
        const fileError = file[0].errors[0];
        switch (true) {
            case fileError.code === fileNameTooLong:
                setErrorMessage(fileError.message)
                break;

            case fileError.code === fileTooLarge:
                setErrorMessage(`File is too large. Maximum size is ${maxFileSize / 1024} KB`,)
                break;

            case fileError.code === fileTooSmall:
                setErrorMessage(`File is too small. Minimum size is ${minFileSize / 1024} KB.`,)
                break;

            default:
                setErrorMessage(
                <FormattedMessage
                    id="case_management.request_form.dropzone_error"
                    values={{
                        link: renderDropzoneLink,
                    }}
                />,
            );
        }
        setError(true);
    };
    const onRemoveFile = (file: File) =>
        setFormValues({ ...formValues, files: files.filter((f) => f.name !== file.name) });
    const onToggleConsent = () =>
        setFormValues({ ...formValues, consentAccepted: !consentAccepted });

    const [isSubmitting, setSubmitting] = React.useState(false);
    const { addSnackbar } = useSnackbar();

    const requestsApi = new RequestsApi(getAuthConfiguration(apiBaseUrl));

    const submit = async () => {
        if (!summary || !description) {
            return;
        }

        setSubmitting(true);
        try {
            const res = await requestsApi.createRequestApiRequestsCreateRequestPost({
                summary,
                description,
                requestTypeId: (issueType as Option).value,
                companyCode: (companyCode as Option).value,
                uploadFiles: files,
            });

            setFormValues({ ...initialState });

            if (onRequestCreated) {
                onRequestCreated({
                    status: defaultRequestStatus,
                    issueId: res.issueId,
                    timestamp: res.timestamp,
                    requestId: res.requestId,
                    summary,
                    issueType: issueType?.value,
                    description,
                });
            }

            addSnackbar({
                type: SnackbarType.Success,
                message: intl.formatMessage({
                    id: "case_management.request_form.submit_success_message",
                }),
                timeout : 10000
            });
        } catch (err) {
            addSnackbar({
                type: SnackbarType.Error,
                message: intl.formatMessage({
                    id: "case_management.request_form.submit_error_message",
                }),
            });
        }
        setSubmitting(false);
    };

    const dropdownClasses = {
        root: styles["dropdown"],
        header: styles["dropdown__header"],
        label: classNames(styles["dropdown__label"], classes?.label),
        arrow: styles["dropdown__arrow"],
        list: styles["dropdown__list"],
        listItem: styles["dropdown__list-item"],
        listItemActive: styles["dropdown__list-item--active"],
        text: styles["dropdown__text"],
    };

    const submitButtonActive =
        consentAccepted && issueType && companyCode && summary && description;

    const UploadeFiles =
        files.length === 0 ? null : (
            <div className={styles["uploaded-files"]}>
                <label className={styles["uploaded-files__label"]}>
                    {intl.formatMessage({
                        id: "case_management.request_form.uploaded-attachments",
                    })}
                    :
                </label>
                <ul className={styles["uploaded-files__list"]}>
                    {files.map((file) => {
                        return (
                            <li key={file.name} className={styles["uploaded-files__list-item"]}>
                                <span className={styles["uploaded-files__file-name"]}>
                                    {file.name}
                                </span>
                                <ButtonIconWithCustomTouchArea
                                    onClick={() => onRemoveFile(file)}
                                    classes={{
                                        icon: styles["uploaded-files__remove-icon"],
                                    }}
                                >
                                    <RemoveIcon />
                                </ButtonIconWithCustomTouchArea>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );

    return (
        <div className={classNames(styles["request-form"], classes?.root)}>
            <div className={classNames(styles["request-form__content"], classes?.content)}>
                <div className={classNames(styles["request-form--primary"], classes?.primary)}>
                    <div className={styles["services-container"]}>
                        <Label required dropdownId="open-cases-select-issue-type">
                            {intl.formatMessage({
                                id: "case_management.request_form.request_type_label",
                            })}
                        </Label>
                        <button
                            className={styles["services-description"]}
                            onClick={() => onOpenContentItem(servicesDescriptionContentID)}
                        >
                            {intl.formatMessage({
                                id: "case_management.services_description",
                            })}
                        </button>
                    </div>
                    <Dropdown
                        disabled={disabled || isSubmitting}
                        classes={dropdownClasses}
                        selectedOption={issueType}
                        options={availableIssueTypes}
                        searchable
                        placeholder={intl.formatMessage({
                            id: "case_management.request_form.request_type_placeholder",
                        })}
                        onChange={onUpdateIssueType}
                        id="open-cases-select-issue-type"
                    />
                    <span className={styles["label-container"]}>
                        <Label required dropdownId="open-cases-select-company-code">
                            {intl.formatMessage({
                                id: "case_management.request_form.company_code_label",
                            })}
                        </Label>
                        <SimpleToolTip
                            className={styles["company-code-tooltip"]}
                            centered={true}
                            content={intl.formatMessage({
                                id: "case_management.request_form.company_code_tooltip",
                            })}
                        >
                            <InfoIcon className={styles["info-icon"]} />
                        </SimpleToolTip>
                    </span>
                    <Dropdown
                        disabled={disabled || isSubmitting}
                        classes={dropdownClasses}
                        selectedOption={companyCode}
                        options={companyCodes}
                        searchable
                        placeholder={intl.formatMessage({
                            id: "case_management.request_form.company_code_placeholder",
                        })}
                        onChange={onUpdateCompanyCode}
                        id="open-cases-select-company-code"
                    />
                </div>
                <div className={classNames(styles["request-form--secondary"], classes?.secondary)}>
                    <Label required dropdownId="open-cases-summary">
                        {intl.formatMessage({
                            id: "case_management.request_form.summary_label",
                        })}
                    </Label>
                    <Input
                        disabled={disabled || isSubmitting}
                        className={styles["summary-input"]}
                        placeholder={intl.formatMessage({
                            id: "case_management.request_form.summary_placeholder",
                        })}
                        value={summary || ""}
                        onChange={onUpdateSummary}
                        id="open-cases-summary"
                    />
                    <Label required dropdownId="open-cases-description">
                        {intl.formatMessage({
                            id: "case_management.request_form.description_label",
                        })}
                    </Label>
                    <TextArea
                        className={styles["description-input"]}
                        disabled={disabled || isSubmitting}
                        disableResize
                        placeholder={intl.formatMessage({
                            id: "case_management.request_form.description_placeholder",
                        })}
                        value={description || ""}
                        onChange={onUpdateDescription}
                        id="open-cases-description"
                    />
                </div>
            </div>
            <Label dropdownId="open-cases-add-attachments">
                {`${intl.formatMessage({
                    id: "case_management.request_form.upload_attchment_label",
                })}:`}
            </Label>
            <Dropzone
                multiple={true}
                accept={acceptableFileTypes}
                disabled={disabled || isSubmitting || files.length >= maxNumbersOfFiles}
                maxSize={maxFileSize}
                minSize={minFileSize}
                onDropRejected={onFileRejected}
                onDrop={onAddFiles}
                maxFiles={maxNumbersOfFiles}
                validator={validateFile}
            >
                {({ getRootProps, getInputProps }) => {
                    return (
                        <>
                            <div
                                className={classNames(styles["file-dropzone"])}
                                {...getRootProps()}
                            >
                                <input {...getInputProps()} id="open-cases-add-attachments" />
                                <P className={styles["file-dropzone__label"]}>
                                    +{" "}
                                    {intl.formatMessage({
                                        id: "case_management.request_form.upload_attchment_placeholder",
                                    })}
                                </P>
                            </div>
                            <p className={styles["error-message"]}>{error && errorMessage}</p>
                        </>
                    );
                }}
            </Dropzone>
            {UploadeFiles}
            <div className={classNames(styles["request-form__footer"], classes?.footer)}>
                <Checkbox
                    onClick={onToggleConsent}
                    checked={consentAccepted}
                    classes={{
                        root: styles["consent-checkbox"],
                        label: styles["consent-checkbox__label"],
                    }}
                    label={intl.formatMessage({
                        id: "case_management.request_form.consent_text",
                    })}
                />
                <Button
                    size="small"
                    disabled={disabled || !submitButtonActive}
                    loading={isSubmitting}
                    onClick={submit}
                >
                    {intl.formatMessage({
                        id: "case_management.request_form.submit",
                    })}
                </Button>
            </div>
        </div>
    );
};

type ConnectedRequestProps = Omit<RequestFormProps, "apiBaseUrl" | "availableIssueTypes">;

const ConnectedRequestForm: React.FC<ConnectedRequestProps> = ({
    classes,
    initialState,
    onRequestCreated,
}) => {
    const dispatch = useDispatch();
    const handleRequestCreated = (request: UserRequest) => {
        dispatch({
            type: ACTIONS.CREATE_REQUEST_SUCCESS,
            payload: request,
        });
        if (onRequestCreated) {
            onRequestCreated(request);
        }
    };

    const apiBaseUrl = useSelector((state) => state.appConfiguration.apiBaseUrl);
    const availableIssueTypes = useSelector((state) => state.requests.availableIssueTypes);

    return (
        <RequestForm
            classes={classes}
            initialState={initialState}
            apiBaseUrl={apiBaseUrl}
            availableIssueTypes={availableIssueTypes.data || []}
            onRequestCreated={handleRequestCreated}
        />
    );
};

export default ConnectedRequestForm;
